import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Car, FileText, Warning, Scales, ShieldCheck, Gavel } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { FAQ } from '@/components/FAQ';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const BuscaApreensao = () => {
  const situacoes = [
    { icon: Car, title: 'Apreensão de veículo financiado' },
    { icon: FileText, title: 'Revisão de contrato de financiamento' },
    { icon: Warning, title: 'Notificação extrajudicial irregular' },
    { icon: Gavel, title: 'Defesa em processo judicial' },
    { icon: Scales, title: 'Purgação da mora' },
    { icon: ShieldCheck, title: 'Discussão de cláusulas abusivas' },
  ];

  const faqItems = [
    {
      question: 'O que é uma ação de busca e apreensão?',
      answer:
        'É uma ação judicial movida por instituições financeiras (bancos, financeiras) para retomar bens (geralmente veículos) quando há inadimplência em contratos de financiamento com alienação fiduciária.',
    },
    {
      question: 'É possível se defender de uma busca e apreensão?',
      answer:
        'Sim. Há diversas estratégias de defesa possíveis, incluindo revisão do contrato, questionamento de cláusulas abusivas, purgação da mora, entre outras. Cada caso deve ser analisado individualmente.',
    },
    {
      question: 'Recebi uma notificação, o que fazer?',
      answer:
        'É fundamental buscar orientação jurídica o quanto antes. A notificação tem prazos importantes e a análise técnica pode identificar irregularidades ou definir a melhor estratégia para o caso.',
    },
    {
      question: 'Ainda é possível manter o veículo?',
      answer:
        'Em muitos casos, sim. Existem alternativas como a purgação da mora (quitação do débito), revisão contratual ou até acordos com a instituição financeira. A análise do contrato é essencial.',
    },
    {
      question: 'A RDC Advogados atende esses casos no Rio de Janeiro?',
      answer:
        'Sim. O escritório está localizado no Centro do Rio de Janeiro e atua em defesas de busca e apreensão com análise técnica e orientação responsável.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="busca-apreensao-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl font-bold playfair tracking-tight mb-6">
              Defesa em Ação de Busca e Apreensão
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Atuação jurídica em defesas de busca e apreensão de veículos e bens, com análise criteriosa da 
              legalidade da medida e defesa consistente dos direitos do cliente.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Opening Text */}
      <section className="py-24 bg-white" data-testid="busca-apreensao-intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto">
            <p className="text-lg text-[#737373] leading-relaxed mb-6">
              Ações de busca e apreensão são medidas judiciais utilizadas por instituições financeiras para 
              retomar bens dados em garantia em contratos de financiamento, sobretudo veículos. Essas ações, 
              embora frequentes, exigem observância rigorosa de requisitos legais e podem apresentar 
              irregularidades passíveis de defesa.
            </p>
            <p className="text-lg text-[#737373] leading-relaxed">
              A RDC Advogados atua na análise técnica dessas demandas, verificando a regularidade do contrato, 
              da notificação e do processo, oferecendo ao cliente uma defesa consistente e as melhores 
              estratégias possíveis para o caso.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Situações */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="busca-apreensao-situacoes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">
              Situações frequentemente analisadas
            </h2>
            <p className="text-lg text-[#737373]">Demandas que podem exigir defesa jurídica especializada</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {situacoes.map((item, index) => (
              <motion.div
                key={item.title || item.id || index}
                {...fadeUp}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white border border-[#2A2A2A]/10 rounded-sm p-6 flex items-center space-x-4"
                data-testid={`situacao-${index}`}
              >
                <item.icon size={32} weight="duotone" className="text-[#A98C67] flex-shrink-0" />
                <span className="text-[#2A2A2A] font-medium">{item.title}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-24 bg-white" data-testid="busca-apreensao-work-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
                Como o escritório atua
              </h2>
              <p className="text-xl text-[#737373] mb-6 leading-relaxed">
                Defesa técnica e estratégica em busca e apreensão
              </p>
              <p className="text-lg text-[#737373] leading-relaxed">
                Cada caso é analisado a partir da documentação contratual, da notificação recebida e do 
                contexto financeiro do cliente. O escritório verifica a regularidade formal do processo, 
                identifica eventuais vícios ou cláusulas abusivas e define a estratégia de defesa mais 
                adequada, sempre com comunicação clara e postura responsável.
              </p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.6 }}>
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85"
                alt="Análise jurídica de contratos"
                className="rounded-sm shadow-lg w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why seek guidance */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="busca-apreensao-guidance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
              Agir rapidamente faz diferença
            </h2>
            <p className="text-xl text-[#737373] mb-8 leading-relaxed">
              Prazos importantes exigem resposta ágil e técnica
            </p>
            <p className="text-lg text-[#737373] leading-relaxed">
              Em ações de busca e apreensão, o tempo é fator crítico. Prazos processuais curtos e a 
              possibilidade de retomada imediata do bem tornam essencial buscar orientação jurídica o quanto 
              antes. Uma análise técnica precisa pode revelar caminhos de defesa e proteger os direitos do 
              cliente de forma efetiva.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-testid="busca-apreensao-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">Perguntas frequentes</h2>
          </motion.div>
          <motion.div {...fadeUp}>
            <FAQ items={faqItems} />
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="busca-apreensao-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold playfair mb-6">
              Recebeu uma ação de busca e apreensão? Aja rapidamente
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Entre em contato com a RDC Advogados para apresentar sua situação e receber uma análise inicial 
              com clareza e seriedade.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button
                  size="lg"
                  data-testid="busca-apreensao-contact-button"
                  className="bg-white text-[#2A2A2A] hover:bg-white/90 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Solicitar análise
                </Button>
              </Link>
              <a href="https://wa.me/552128880258" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  data-testid="busca-apreensao-whatsapp-button"
                  className="bg-[#25D366] text-white hover:bg-[#20BA5A] rounded-sm font-medium px-8 py-6 text-base border-0"
                >
                  Falar pelo WhatsApp
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BuscaApreensao;
